import { useMemo, useRef, useState } from "react";
import { Link2, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";
import ButtonStyleSelect from "@/components/ButtonStyleSelect";

/**
 * Quick Order — garment fabric + lining chooser.
 *
 * One Fabric row + one Lining row per garment that's part of the current
 * order (Jacket, Pant, Vest, Shirt, Overcoat). Every fabric pool exposes a
 * Book filter + free-text search so the user can reach ANY code in the
 * catalog (RC suits, RC overcoats, RC shirts, MX wholesale, and the full
 * lining catalog).
 *
 * Three "Match all" actions:
 *   - Match fabric across garments (jacket → pant + vest + overcoat)
 *   - Match lining across garments (jacket → pant + vest + overcoat)
 *
 * Props:
 *   fabricByGarment   — { jacket, pant, vest, shirt, overcoat } current rc_codes
 *   setFabricByGarment — replace entire map
 *   liningByGarment   — same shape, for lining codes (optional)
 *   setLiningByGarment — replace entire lining map (optional)
 *   garmentType       — current order type ("2-Piece Suit", "Tuxedo", etc.)
 */
const GARMENT_TO_CATEGORY = {
  jacket:   { catalog: "suits",     mxAllowed: true,  label: "Jacket" },
  pant:     { catalog: "suits",     mxAllowed: true,  label: "Pant" },
  vest:     { catalog: "suits",     mxAllowed: true,  label: "Vest" },
  overcoat: { catalog: "overcoats", mxAllowed: false, label: "Overcoat" },
  shirt:    { catalog: "shirts",    mxAllowed: false, label: "Shirt" },
};

// Exported so other Step-3 modules can mirror the same visibility logic
// without duplicating the mapping (e.g. cascades that should respect the
// active garment type).
export function visibleGarmentsFor(garmentType) {
  switch ((garmentType || "").toLowerCase()) {
    // Human labels (Quick Order + Status pages)
    case "tuxedo":
    case "3-piece suit":
      return ["jacket", "pant", "vest"];
    case "2-piece suit":
    case "suit":
      return ["jacket", "pant"];
    case "jacket":
    case "sport coat":
    case "blazer":
    case "sport coat / blazer":
      return ["jacket"];
    case "trousers":
    case "pants":
    case "pant":
      return ["pant"];
    case "vest":
      return ["vest"];
    case "shirt":
    case "dress shirt":
      return ["shirt"];
    case "overcoat":
      return ["overcoat"];
    // POS internal values (slug from `GARMENT_TYPES[].value`)
    case "3piece":
      return ["jacket", "pant", "vest"];
    case "2piece":
      return ["jacket", "pant"];
    case "trouser":
      return ["pant"];
    default:
      // Fallback: try a substring match so misspellings still pick the
      // right block (e.g. "Tuxedo (Notch)" → tux → jacket+pant+vest).
      // Otherwise default to the full bespoke 3-piece set.
      return ["jacket", "pant", "vest"];
  }
}

/**
 * Generic searchable code picker — drives both the Fabric and Lining rows.
 * No slice cap: every item that matches the filter is visible.
 */
function CodeRow({
  label,
  accent = "purple",
  value,
  onChange,
  items,
  testId,
  showSourceBadge = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [bookFilter, setBookFilter] = useState("");

  const bookOptions = useMemo(() => {
    const set = new Set();
    for (const it of items) if (it.book) set.add(it.book);
    return Array.from(set).sort();
  }, [items]);

  const matches = useMemo(() => {
    let pool = items;
    if (bookFilter) pool = pool.filter((it) => (it.book || "") === bookFilter);
    const needle = q.trim().toLowerCase();
    if (!needle) return pool;
    return pool.filter((it) => {
      return (
        (it.rc_code || "").toLowerCase().includes(needle) ||
        (it.book || "").toLowerCase().includes(needle) ||
        (it.color || "").toLowerCase().includes(needle) ||
        (it.pattern || "").toLowerCase().includes(needle) ||
        (it.composition || "").toLowerCase().includes(needle)
      );
    });
  }, [items, q, bookFilter]);

  const selected = items.find((f) => f.rc_code === value);

  const accentDot = {
    purple: "bg-purple-500",
    sky:    "bg-sky-500",
  };
  const accentCode = {
    purple: "text-amber-400",
    sky:    "text-sky-400",
  };
  const accentChip = {
    purple: "border-purple-500/40 hover:border-purple-500/70",
    sky:    "border-sky-500/40 hover:border-sky-500/70",
  };

  return (
    <div data-testid={testId} className="flex items-center gap-3">
      <label className="text-xs text-slate-400 w-24 shrink-0 flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full ${accentDot[accent]}`} />
        {label}
      </label>
      <div className="flex-1 relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`w-full text-left bg-[#0a1628] border ${accentChip[accent]} rounded-md px-3 h-11 transition-colors flex items-center justify-between gap-2`}
          data-testid={`${testId}-trigger`}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <code className={`font-mono font-semibold text-sm ${accentCode[accent]} shrink-0`}>
                {selected.rc_code}
              </code>
              {showSourceBadge && selected.source === "MX" && (
                <span className="text-[10px] text-purple-300 bg-purple-500/15 px-1.5 py-0.5 rounded font-mono shrink-0">
                  MX
                </span>
              )}
              <span className="text-slate-300 text-sm truncate">
                {[
                  selected.book ? `Book ${selected.book}` : null,
                  selected.color,
                  selected.pattern,
                ].filter(Boolean).join(" · ") || selected.name || ""}
              </span>
            </span>
          ) : (
            <span className="text-slate-500 text-sm">— Select {label.toLowerCase()} —</span>
          )}
          <Search size={14} className="text-slate-500 shrink-0" />
        </button>
        {open && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-[#0a1628] border border-white/10 rounded-md shadow-xl max-h-[28rem] overflow-auto">
            <div className="sticky top-0 z-10 bg-[#0a1628] border-b border-white/10 p-2 space-y-1.5">
              <div className="flex items-center gap-2">
                <Search size={14} className="text-slate-500 shrink-0" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search rc_code, book, color, pattern…"
                  className="bg-transparent border-0 h-7 text-sm focus-visible:ring-0 px-0"
                  autoFocus
                  data-testid={`${testId}-search`}
                />
                <span className="text-[11px] text-slate-400 font-mono shrink-0">
                  {matches.length}
                </span>
              </div>
              <Select
                value={bookFilter || "__all__"}
                onValueChange={(v) => setBookFilter(v === "__all__" ? "" : v)}
              >
                <SelectTrigger
                  className="h-7 text-xs bg-transparent border-white/10"
                  data-testid={`${testId}-book-filter`}
                >
                  <SelectValue placeholder="All books" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All books ({bookOptions.length})</SelectItem>
                  {bookOptions.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {matches.map((f) => {
              const codeColor = accent === "sky" ? "text-sky-300" : "text-amber-300";
              const codeBg = accent === "sky" ? "bg-sky-500/10" : "bg-amber-500/10";
              const rowHover = accent === "sky" ? "hover:bg-sky-500/15" : "hover:bg-purple-500/15";
              return (
                <button
                  key={`${f.rc_code}-${f.source || "rc"}`}
                  type="button"
                  onClick={() => {
                    onChange(f.rc_code);
                    setOpen(false);
                    setQ("");
                  }}
                  className={`w-full text-left px-3 py-2 border-b border-white/5 last:border-0 ${rowHover}`}
                  data-testid={`${testId}-opt-${f.rc_code}`}
                >
                  {/* Top row: code badge + book + price + MX tag */}
                  <div className="flex items-center gap-2 text-xs">
                    <code className={`font-mono font-semibold ${codeColor} ${codeBg} px-1.5 py-0.5 rounded shrink-0`}>
                      {f.rc_code}
                    </code>
                    {showSourceBadge && f.source === "MX" && (
                      <span className="text-[10px] text-purple-300 bg-purple-500/15 px-1.5 py-0.5 rounded font-mono shrink-0">
                        MX
                      </span>
                    )}
                    {f.book && (
                      <span className="text-[11px] text-slate-400 shrink-0">Book {f.book}</span>
                    )}
                    {f.price && (
                      <span className="ml-auto text-[11px] text-emerald-400 font-mono">${f.price}</span>
                    )}
                  </div>
                  {/* Bottom row: color · pattern · composition · weight */}
                  <div className="mt-1 text-[11px] text-slate-300 truncate">
                    {f.color || "—"}
                    {f.pattern ? <> · <span className="text-slate-400">{f.pattern}</span></> : null}
                    {f.composition ? <> · <span className="text-slate-500">{f.composition}</span></> : null}
                    {f.weight ? <> · <span className="text-slate-500">{f.weight}</span></> : null}
                  </div>
                </button>
              );
            })}
            {!matches.length && (
              <p className="text-center text-slate-500 text-sm py-6">No matches</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GarmentFabricSection({
  fabricByGarment = {},
  setFabricByGarment,
  liningByGarment = {},
  setLiningByGarment,
  garmentType,
  // Optional button cascade — when provided, exposes a "Match buttons" action
  // that copies the source button code into every per-garment button slot.
  buttonSourceLabel,        // human label for the disabled-tooltip ("Jacket button")
  buttonSourceValue,        // current source value (string or empty)
  onMatchAllButtons,        // () => void; parent owns the actual cascade
  // Optional per-garment button pickers. When `onButtonChange` is provided,
  // each garment renders a third row with a full-catalog button selector.
  buttonByGarment = {},     // { jacket, pant, vest, shirt } → current code/value
  onButtonChange,           // (garment, value) => parent routes to its styling state
  testIdPrefix = "qo-garment-fabric",
}) {
  const { catalog, loaded } = useFabricCatalog();
  const garments = visibleGarmentsFor(garmentType);

  // Tag MX rows with `source: "MX"` so the picker can badge them and use the
  // composite key (rc_code|source) to disambiguate any cross-catalog dupes.
  const mxTagged = useMemo(() => {
    return (catalog?.mx || []).map((f) => ({ ...f, source: "MX" }));
  }, [catalog]);

  // RC suits + MX merged for jacket/pant/vest. Overcoats and shirts stay
  // single-source because MX doesn't categorise into those buckets.
  const suitsMerged = useMemo(() => {
    const rc = (catalog?.rc?.suits || []).map((f) => ({ ...f, source: "RC" }));
    return [...rc, ...mxTagged];
  }, [catalog, mxTagged]);

  const itemsForGarment = (g) => {
    const meta = GARMENT_TO_CATEGORY[g];
    if (meta.catalog === "suits") return suitsMerged;
    if (meta.catalog === "overcoats") return (catalog?.rc?.overcoats || []).map((f) => ({ ...f, source: "RC" }));
    if (meta.catalog === "shirts") return (catalog?.rc?.shirts || []).map((f) => ({ ...f, source: "RC" }));
    return [];
  };

  const liningItems = useMemo(() => {
    return (catalog?.rc?.linings || []).map((l) => ({ ...l, source: "RC" }));
  }, [catalog]);

  // Tracks the LAST value pushed by each Match cascade. Future Match clicks
  // skip a garment whose current value differs from this last-cascaded value
  // (= a manual override). null on first Match → always overwrites.
  const lastMatchedRef = useRef({ fabric: null, lining: null, button: null });

  const matchAllFabric = () => {
    const src = fabricByGarment.jacket || fabricByGarment.overcoat;
    if (!src) return;
    const lastSrc = lastMatchedRef.current.fabric;
    const next = { ...fabricByGarment };
    for (const g of garments) {
      if (g === "shirt") continue; // shirt uses a different fabric catalog
      const cur = next[g];
      // Sticky override: once a Match has cascaded, future Match clicks
      // skip garments whose value differs from the LAST cascaded value
      // (= the user manually overrode them after that prior cascade).
      // The very first Match — where lastSrc is null — always overwrites.
      if (lastSrc !== null && cur && cur !== lastSrc) continue;
      next[g] = src;
    }
    lastMatchedRef.current.fabric = src;
    setFabricByGarment(next);
  };

  const matchAllLining = () => {
    if (!setLiningByGarment) return;
    const src = liningByGarment.jacket || liningByGarment.overcoat;
    if (!src) return;
    const lastSrc = lastMatchedRef.current.lining;
    const next = { ...liningByGarment };
    for (const g of garments) {
      const cur = next[g];
      if (lastSrc !== null && cur && cur !== lastSrc) continue;
      next[g] = src;
    }
    lastMatchedRef.current.lining = src;
    setLiningByGarment(next);
  };

  const canMatchFabric =
    (fabricByGarment.jacket || fabricByGarment.overcoat) &&
    garments.filter((g) => g !== "shirt").length > 1;

  const canMatchLining =
    !!setLiningByGarment &&
    (liningByGarment.jacket || liningByGarment.overcoat) &&
    garments.length > 1;

  const canMatchButtons =
    !!onMatchAllButtons && !!buttonSourceValue && garments.length > 1;

  if (!garments.length) return null;

  return (
    <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
      <div className="p-4 flex items-center justify-between flex-wrap gap-2 border-b border-white/10">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <span className="w-1.5 h-5 bg-amber-400 rounded-full" /> Fabric, Lining &amp; Buttons
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            size="sm"
            onClick={matchAllFabric}
            disabled={!canMatchFabric}
            className="h-8 text-xs bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40"
            data-testid={`${testIdPrefix}-match-all-btn`}
            title="Copy Jacket / Overcoat fabric to Pant + Vest"
          >
            <Link2 size={12} className="mr-1.5" /> Match fabric
          </Button>
          {setLiningByGarment ? (
            <Button
              type="button"
              size="sm"
              onClick={matchAllLining}
              disabled={!canMatchLining}
              className="h-8 text-xs bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40"
              data-testid={`${testIdPrefix}-match-lining-btn`}
              title="Copy Jacket / Overcoat lining to Pant + Vest"
            >
              <Link2 size={12} className="mr-1.5" /> Match lining
            </Button>
          ) : null}
          {onMatchAllButtons ? (
            <Button
              type="button"
              size="sm"
              onClick={onMatchAllButtons}
              disabled={!canMatchButtons}
              className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40"
              data-testid={`${testIdPrefix}-match-buttons-btn`}
              title={buttonSourceValue
                ? `Copy ${buttonSourceLabel || "Jacket"} button to Pant + Vest + Shirt`
                : `Pick a ${(buttonSourceLabel || "Jacket").toLowerCase()} button first`}
            >
              <Link2 size={12} className="mr-1.5" /> Match buttons
            </Button>
          ) : null}
        </div>
      </div>
      <div className="p-4 space-y-4">
        {!loaded ? (
          <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading fabric catalog…
          </div>
        ) : (
          garments.map((g) => {
            const meta = GARMENT_TO_CATEGORY[g];
            return (
              <div
                key={g}
                className="bg-[#0a1628]/40 border border-white/5 rounded-lg p-3 space-y-2.5"
                data-testid={`${testIdPrefix}-${g}-block`}
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
                  <span className="w-1 h-3 bg-amber-400/60 rounded-full" />
                  {meta.label}
                </div>
                <CodeRow
                  label={`${meta.label} fabric`}
                  accent="purple"
                  value={fabricByGarment[g] || ""}
                  onChange={(v) => setFabricByGarment({ ...fabricByGarment, [g]: v })}
                  items={itemsForGarment(g)}
                  testId={`${testIdPrefix}-${g}`}
                  showSourceBadge={meta.mxAllowed}
                />
                {setLiningByGarment ? (
                  <CodeRow
                    label={`${meta.label} lining`}
                    accent="sky"
                    value={liningByGarment[g] || ""}
                    onChange={(v) => setLiningByGarment({ ...liningByGarment, [g]: v })}
                    items={liningItems}
                    testId={`${testIdPrefix}-${g}-lining`}
                  />
                ) : null}
                {onButtonChange ? (
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-slate-400 w-24 shrink-0 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {meta.label} button
                    </label>
                    <div className="flex-1">
                      <ButtonStyleSelect
                        label=""
                        category={g === "shirt" ? "shirt" : "suit"}
                        value={buttonByGarment[g] || ""}
                        onChange={(v) => onButtonChange(g, v)}
                        testId={`${testIdPrefix}-${g}-button`}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
